import { fetchUtils } from "react-admin";

const apiUrl = process.env.REACT_APP_API_URL + "/api/admin/v1/auths"; // "http://127.0.0.1:3300";
const tokenName = process.env.REACT_APP_TOKEN_NAME;
const httpClient = (url, options = {}) => {
  if (!options.headers) {
    options.headers = new Headers({ Accept: "application/json" });
  }
  const token = localStorage.getItem(tokenName);
  if (token) {
    options.headers.set("Authorization", `Bearer ${token}`);
  }
  return fetchUtils.fetchJson(url, options);
};

const authProvider = {
  // called when the user attempts to log in
  login: ({ username, password }) => {
    const url = `${apiUrl}/login`;
    return httpClient(url, {
      method: "POST",
      body: JSON.stringify({ username, password }),
    }).then(({ headers, json }) => {
      localStorage.setItem(tokenName, json.data);
      return Promise.resolve();
    });
  },
  // called when the user clicks on the logout button
  logout: () => {
    if (localStorage[tokenName]) {
      const url = `${apiUrl}/logout`;
      return httpClient(url, {
        method: "POST",
        body: "{}",
      })
        .then(({ headers, json }) => {
          localStorage.removeItem(tokenName);
          localStorage.removeItem("authIdentity");
          try {
            window.dispatchEvent(new CustomEvent("authIdentityUpdated", { detail: null }));
          } catch (e) {}
          return Promise.resolve();
        })
        .catch((err) => {
          // even if server logout fails (network/server issue), ensure local cleanup
          localStorage.removeItem(tokenName);
          localStorage.removeItem("authIdentity");
          try {
            window.dispatchEvent(new CustomEvent("authIdentityUpdated", { detail: null }));
          } catch (e) {}
          return Promise.resolve();
        });
    }
    return Promise.resolve();
  },
  // called when the API returns an error
  checkError: ({ status }) => {
    if (status === 401) {
      // clear token and cached identity on unauthorized
      localStorage.removeItem(tokenName);
      localStorage.removeItem("authIdentity");
      try {
        window.dispatchEvent(new CustomEvent("authIdentityUpdated", { detail: null }));
      } catch (e) {}
      return Promise.reject();
    }
    return Promise.resolve();
  },
  // called when the user navigates to a new location, to check for authentication
  checkAuth: () => {
    return localStorage.getItem(tokenName)
      ? Promise.resolve()
      : Promise.reject();
  },
  // called when the user navigates to a new location, to check for permissions / roles
  getPermissions: () => {
    const rolesUrl =
      process.env.REACT_APP_API_URL + "/api/admin/v1/user-access/roles";
    return httpClient(rolesUrl, {
      method: "GET",
    }).then(({ headers, json }) => {
      // localStorage.setItem(tokenName, json.data);
      if (json.roles && json.roles.length) {
        return Promise.resolve(json.roles[0]);
      } else {
        return Promise.reject();
      }
    });
  },
  getIdentity: () => {
    const url = process.env.REACT_APP_API_URL + "/api/admin/v1/auths/identity";

    // Try returning cached identity immediately (non-blocking background refresh)
    try {
      const cachedRaw = localStorage.getItem("authIdentity");
      if (cachedRaw) {
        const cached = JSON.parse(cachedRaw);
        if (cached && cached.id) {
          // Background refresh to update cache without blocking UI
          httpClient(url, { method: "GET" })
            .then(({ json }) => {
              if (json && json.id) {
                const fresh = {
                  id: json.id,
                  companyID: json.company_id,
                  fullName: json.full_name,
                  avatar: json.avatar,
                };
                try {
                  localStorage.setItem("authIdentity", JSON.stringify(fresh));
                } catch (e) {
                  /* ignore storage errors */
                }
                // Notify app that identity was refreshed
                try {
                  window.dispatchEvent(new CustomEvent("authIdentityUpdated", { detail: fresh }));
                } catch (e) {}
              }
            })
            .catch((err) => {
              console.warn("authProvider.getIdentity: background refresh failed", err);
              const status = (err && err.status) || (err && err.response && err.response.status) || null;
              if (status === 401) {
                localStorage.removeItem("authIdentity");
                localStorage.removeItem(tokenName);
                try {
                  window.dispatchEvent(new CustomEvent("authIdentityUpdated", { detail: null }));
                } catch (e) {}
              }
            });

          console.info("authProvider.getIdentity: returning cached identity");
          return Promise.resolve(cached);
        }
      }
    } catch (e) {
      console.warn("authProvider.getIdentity: failed to parse cached identity", e);
    }

    // No cached identity - perform normal fetch
    return httpClient(url, { method: "GET" }).then(({ json }) => {
      if (json && json.id) {
        const fresh = {
          id: json.id,
          companyID: json.company_id,
          fullName: json.full_name,
          avatar: json.avatar,
        };
        try {
          localStorage.setItem("authIdentity", JSON.stringify(fresh));
        } catch (e) {
          /* ignore storage errors */
        }
        console.info("authProvider.getIdentity: returning fresh identity");
        return Promise.resolve(fresh);
      } else {
        return Promise.reject();
      }
    });
  },
};

export default authProvider;
